import {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import fr from '../locales/fr/translation.json';
import en from '../locales/en/translation.json';
import webExtraTranslations from './webExtraTranslations';
import webRemainingTranslations from './webRemainingTranslations';
import webGitlabTranslations from './webGitlabTranslations';

const normalise = (value) => value.trim().replace(/\s+/g, ' ');
const frToEn = new Map();
const enToFr = new Map();

for (const key of Object.keys(fr)) {
  const french = fr[key];
  const english = en[key];
  if (
    typeof french !== 'string' ||
    typeof english !== 'string' ||
    french.includes('{{') ||
    english.includes('{{')
  ) {
    continue;
  }

  const frenchKey = normalise(french);
  const englishKey = normalise(english);
  if (!frenchKey || !englishKey || frenchKey === englishKey) continue;

  if (!frToEn.has(frenchKey)) frToEn.set(frenchKey, english);
  if (!enToFr.has(englishKey)) enToFr.set(englishKey, french);
}

for (const [french, english] of Object.entries({
  ...webExtraTranslations,
  ...webRemainingTranslations,
  ...webGitlabTranslations,
})) {
  frToEn.set(normalise(french), english);
  enToFr.set(normalise(english), french);
}

const ignoredSelector =
  '[data-i18n-skip], script, style, code, pre, [contenteditable=true]';
const translatedAttributes = ['placeholder', 'title', 'aria-label'];

const translateValue = (value, language) => {
  if (!value || !value.trim()) return value;
  const dictionary = language === 'en' ? frToEn : enToFr;
  const normalisedValue = normalise(value);
  const unquotedValue = normalisedValue.replace(/^\|\$/g, '');
  const translated =
    dictionary.get(normalisedValue) ??
    (unquotedValue !== normalisedValue
      ? dictionary.get(unquotedValue)
      : undefined);
  if (!translated) return value;

  const leadingSpace = value.match(/^\s*/)?.[0] ?? '';
  const trailingSpace = value.match(/\s*$/)?.[0] ?? '';
  return `${leadingSpace}${translated}${trailingSpace}`;
};

const shouldIgnore = (element) =>
  !element || Boolean(element.closest(ignoredSelector));

const translateTextNode = (node, language) => {
  if (shouldIgnore(node.parentElement)) return;
  const translated = translateValue(node.nodeValue, language);
  if (translated !== node.nodeValue) node.nodeValue = translated;
};

const translateElementAttributes = (element, language) => {
  if (shouldIgnore(element)) return;
  for (const attribute of translatedAttributes) {
    if (!element.hasAttribute(attribute)) continue;
    const value = element.getAttribute(attribute);
    const translated = translateValue(value, language);
    if (translated !== value) element.setAttribute(attribute, translated);
  }
};

const translateSubtree = (root, language) => {
  if (!root) return;
  if (root.nodeType === Node.TEXT_NODE) {
    translateTextNode(root, language);
    return;
  }
  if (root.nodeType !== Node.ELEMENT_NODE) return;

  translateElementAttributes(root, language);
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let textNode = walker.nextNode();
  while (textNode) {
    translateTextNode(textNode, language);
    textNode = walker.nextNode();
  }
  root.querySelectorAll('*').forEach((element) => {
    translateElementAttributes(element, language);
  });
};

const AppTranslationBridge = () => {
  const {i18n} = useTranslation();
  const language = i18n.resolvedLanguage?.startsWith('en') ? 'en' : 'fr';

  useEffect(() => {
    document.documentElement.lang = language;
    translateSubtree(document.body, language);

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'characterData') {
          translateTextNode(mutation.target, language);
          continue;
        }
        if (mutation.type === 'attributes') {
          translateElementAttributes(mutation.target, language);
          continue;
        }
        mutation.addedNodes.forEach((node) => translateSubtree(node, language));
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: translatedAttributes,
    });

    return () => observer.disconnect();
  }, [language]);

  return null;
};

export default AppTranslationBridge;
